import { X } from 'lucide-react';
import { debounce } from 'lodash';
import { useTransition } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { InputSearch } from 'src/components/ui/input-search';
import { Button } from 'src/components/ui/button';
import { DepositStatusEnum, SyncStatusEnum } from 'src/graphql/schema';
import { useAssetsContext, depositStatusOptions, syncStatusOptions } from 'src/containers/Deposits';

const DepositStatusSelect = () => {
  const {
    filters: { depositStatus },
    setDepositStatus,
  } = useAssetsContext();

  const onChange = (value: string) => setDepositStatus(value !== 'all' ? (value as DepositStatusEnum) : undefined);

  return (
    <Select onValueChange={onChange} value={depositStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 *:data-[slot=select-value]:hidden sm:*:data-[slot=select-value]:block">
        <SelectValue placeholder="Deposit status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Deposit status</SelectLabel>
          {depositStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const SyncStatusSelect = () => {
  const {
    filters: { syncStatus },
    setSyncStatus,
  } = useAssetsContext();

  const onChange = (value: string) => setSyncStatus(value !== 'all' ? (value as SyncStatusEnum) : undefined);

  return (
    <Select onValueChange={onChange} value={syncStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 *:data-[slot=select-value]:hidden sm:*:data-[slot=select-value]:block">
        <SelectValue placeholder="Sync Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Sync Status</SelectLabel>
          {syncStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const AssetsFilters = () => {
  const [isPending, startTransition] = useTransition();
  const {
    setQueryFilter,
    setInitialFilters,
    filters: { depositStatus, syncStatus, queryFilter },
  } = useAssetsContext();

  const onChange = debounce((e) => startTransition(() => setQueryFilter(e.target.value)), 500);

  const onClearSearch = () => setQueryFilter(undefined);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <SyncStatusSelect />
        <DepositStatusSelect />
        <InputSearch
          placeholder="Search"
          className="max-w-[280px] w-full"
          onChange={onChange}
          isLoading={isPending}
          onClear={onClearSearch}
        />
      </div>
      {[depositStatus, syncStatus, queryFilter].some(Boolean) && (
        <Button variant="ghost" className="px-0" onClick={setInitialFilters}>
          <X /> Clear all
        </Button>
      )}
    </div>
  );
};

export default AssetsFilters;
